import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./RozetkaPage.css";
import RozetkaReceipts from "../components/marketplace/RozetkaReceipts";
import RozetkaTopBar from "../components/marketplace/RozetkaTopBar";
import MarketplacePricingTab from "../components/marketplace/pricing/MarketplacePricingTab";
import useMarketplacePricingMode from "../components/marketplace/pricing/useMarketplacePricingMode";
import MarketplaceSendersTable from "../components/marketplace/senders/MarketplaceSendersTable";
import { getBusinessOrganizations } from "../api/businessOrganizationsApi";
import { getBusinessStores } from "../api/suppliersApi";
import { getEnterprises } from "../api/enterpriseApi";
import {
    createRozetkaAccount,
    createRozetkaOrderStatusMapping,
    createRozetkaPaymentMapping,
    createRozetkaProduct,
    createSalesdrivePaymentMethod,
    deleteRozetkaOrderStatusMapping,
    deleteRozetkaPaymentMapping,
    deleteSalesdrivePaymentMethod,
    downloadRozetkaSkippedReport,
    dryRunRozetkaOrders,
    generateRozetkaFeed,
    getRozetkaAccounts,
    getRozetkaJobs,
    getRozetkaOrders,
    getRozetkaOrderStatuses,
    getRozetkaOrderStatusMappings,
    getRozetkaPaymentMappings,
    getRozetkaPaymentMethods,
    getRozetkaPriceQuarantine,
    getRozetkaSalesdrivePaymentMethods,
    getRozetkaSalesdriveStatuses,
    getSalesdrivePaymentMethodsDirectory,
    healthcheckRozetkaAccount,
    healthcheckRozetkaOrders,
    repairRozetkaMainImages,
    syncRozetkaModerationStatus,
    syncRozetkaProductLinks,
    syncRozetkaStockPrices,
    updateRozetkaAccount,
    updateRozetkaOrderStatusMapping,
    updateRozetkaPaymentMapping,
    updateRozetkaProductContent,
    updateSalesdrivePaymentMethod,
} from "../api/rozetkaApi";

const SOURCE_MODES = [
    { value: "sajt_and_utm", label: "sajt + UTM" },
    { value: "sajt_only", label: "только sajt" },
    { value: "custom_fields", label: "custom fields" },
];

const emptyRozetkaAccount = {
    account_code: "",
    account_name: "",
    business_organization_id: "",
    is_active: true,
    catalog_enabled: true,
    stock_price_enabled: true,
    base_url: "https://api-seller.rozetka.com.ua",
    default_language: "uk",
    feed_stock_source: "pricelist",
    price_guard_ignore_check: false,
    product_link_scan_limit: 1000,
    feed_product_limit: 1000,
    catalog_schedule_interval_sec: 3600,
    stock_price_schedule_interval_sec: 900,
    status: "draft",
    notes: "",
    api_login: "",
    api_password: "",
    api_password_set: false,
    api_token: "",
    api_token_set: false,
    // --- Заказы (Этап 2, Фаза 3) ---
    business_store_id: "",
    salesdrive_api_enterprise_code: "",
    salesdrive_source_label: "rozetka.com.ua",
    orders_enabled: false,
    order_fetch_interval_seconds: 300,
    order_initial_lookback_days: 7,
    order_overlap_minutes: 10,
    order_live_send_enabled: false,
    mark_received_after_salesdrive_create: false,
    create_canceled_orders: false,
    source_field_mode: "sajt_and_utm",
    receipt_link_sync_enabled: false,
    payment_status_sync_enabled: false,
};

const emptyPaymentMappingDraft = {
    external_payment_method_id: "",
    external_payment_method_name: "",
    salesdrive_handler_value: "",
    initial_status_id: "",
    has_postpay: false,
    payment_policy: "manual_review",
    is_active: true,
    notes: "",
};

const emptySdPaymentMethodDraft = { name: "", code: "", is_active: true };

const emptyStatusMappingDraft = {
    source_status_id: "",
    source_status_name: "",
    target_action: "update_order",
    target_status: "",
    requires_ttn: false,
    is_active: true,
    notes: "",
};

const Field = ({ label, children }) => (
    <label className="rzm-field">
        <span className="rzm-field-label">{label}</span>
        {children}
    </label>
);

const CheckboxField = ({ label, checked, onChange }) => (
    <label className="rzm-check">
        <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
        {label}
    </label>
);

// Краткие описания полей/кнопок по вкладкам -- для HelpTip ("?" в углу карточки).
// Заполняется постепенно; вкладки без записи здесь просто не показывают иконку.
const HELP_TEXT = {
    orders: {
        title: "Заказы Rozetka → SalesDrive",
        items: [
            ["BusinessStore (offer scope)", "ваш склад/магазин, «свой» для этого аккаунта — по нему резолвится товар/остаток/поставщик заказа."],
            ["SalesDrive enterprise_code", "из какого предприятия брать API-токен SalesDrive для отправки заказов."],
            ["Метка источника (sajt)", "значение поля sajt в заказе SalesDrive — метка, что заказ пришёл с Rozetka."],
            ["Source-поля", "какие поля-источники заполнять в заказе: sajt+UTM / только sajt / custom fields."],
            ["Интервал polling, сек", "как часто шедулер опрашивает Rozetka на новые/изменённые заказы."],
            ["Lookback (дней, bootstrap)", "на сколько дней назад читать историю при первом включении polling-а."],
            ["Overlap, мин", "нахлёст окна опроса между тиками, чтобы не терять заказы на границе интервала."],
            ["Polling заказов включён", "главный рубильник — без него заказы этого аккаунта вообще не читаются."],
            ["Live send в SalesDrive", "реальная отправка в SalesDrive; выключено — только чтение и проверка маппинга (безопасная обкатка)."],
            ["Создавать по отменённым", "заводить ли в SalesDrive заказ, уже отменённый на Rozetka."],
            ["Синк статусов оплаты", "отдельный опрос payment-status для отображения факта оплаты (кэш 5 мин)."],
            ["Синк возвратов клиентов", "отдельный шедулер заявок на возврат (своя вкладка «Возвраты»)."],
            ["Сохранить настройки заказов", "сохраняет форму выше."],
            ["Healthcheck заказов", "живой тестовый запрос к Rozetka API — проверка токена и прав на Orders."],
            ["Dry-run", "пробный забор 10 заказов: нормализация и маппинг без записи в БД и без отправки в SalesDrive."],
        ],
    },
    payments: {
        title: "Виды оплат Rozetka → SalesDrive",
        items: [
            ["Справочник способов оплаты SalesDrive", "в разделе Rozetka пока используется общий справочник имя+код, который ведётся вручную. SalesDrive также предоставляет GET /api/payment-methods/; Kasta загружает этот каталог напрямую по ключу выбранного предприятия."],
            ["Код в справочнике", "то самое значение, которое реально уходит в заказ SalesDrive полем payment_method — должно совпадать с тем, что ожидает обработчик SalesDrive."],
            ["Вид оплаты Rozetka", "из живого справочника аккаунта (GET /market-payment-methods/list). Появится, когда в кабинете Rozetka откроют доступ к Orders API для этого токена."],
            ["SalesDrive способ оплаты", "выбор из справочника слева — сохраняет именно код (то, что уходит в SalesDrive), название только для отображения."],
            ["Начальный statusId в SalesDrive", "если задано — заказ с этим видом оплаты создаётся сразу с этим статусом вместо дефолтного."],
            ["Правило обработки оплаты", "постоплата выпускается сразу; онлайн-предоплата ждёт paid из API Rozetka; ручная предоплата ждёт подтверждения менеджера в SalesDrive; ручная проверка блокирует заказ до настройки правила."],
            ["Постоплата", "только для наложенного платежа: проставляет hasPostpay перевозчику. Должна соответствовать правилу «Постоплата»."],
            ["Активен", "неактивный маппинг не используется: неизвестный способ оплаты попадёт в ручную проверку, а не в автоматическую отгрузку."],
        ],
    },
    statuses: {
        title: "Маппинг статусов SalesDrive → Rozetka",
        items: [
            ["Статус SalesDrive", "статус заказа SalesDrive, при котором сработает правило; выбор из списка (с исправленными названиями — id 9/19/22 и т.д.)."],
            ["Действие", "update_order — реально отправить статус/ттн в Rozetka; skip — намеренно ничего не отправлять для этого статуса."],
            ["Целевой статус Rozetka", "код статуса Rozetka, который уйдёт в PUT /orders/{id}, если Действие = update_order."],
            ["Требует ттн", "если включено, а ттн ещё не известен на момент события — правило не сработает сейчас (не шлём заведомо невалидный запрос), сработает позже, когда ттн появится."],
            ["Активен", "неактивные правила не участвуют в подборе вообще."],
            ["Добавить маппинг", "сохраняет строку."],
        ],
    },
};

const HelpTip = ({ section }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const help = HELP_TEXT[section];

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    if (!help) return null;
    return (
        <div className="rzm-help" ref={wrapRef}>
            <button
                type="button"
                className="rzm-help-btn"
                aria-label="Справка по полям"
                onClick={() => setOpen((v) => !v)}
            >
                ?
            </button>
            {open && (
                <div className="rzm-help-pop">
                    <div className="rzm-help-pop-head">
                        <strong>{help.title}</strong>
                        <button type="button" className="rzm-help-close" aria-label="Закрыть" onClick={() => setOpen(false)}>
                            ×
                        </button>
                    </div>
                    <dl>
                        {help.items.map(([label, text]) => (
                            <React.Fragment key={label}>
                                <dt>{label}</dt>
                                <dd>{text}</dd>
                            </React.Fragment>
                        ))}
                    </dl>
                </div>
            )}
        </div>
    );
};

const jobTypeLabel = (jobType) => {
    switch (jobType) {
        case "feed_generate":
            return "Фид контента";
        case "rozetka_product_links_sync":
            return "Связки товаров";
        case "rozetka_stock_price_sync":
            return "API цены/остатки";
        case "rozetka_main_image_repair":
            return "Исправление главных фото";
        default:
            return jobType;
    }
};

const DIAGNOSTIC_LABELS = {
    skip_missing_sku: "нет SKU",
    skip_missing_name: "нет названия",
    skip_missing_image: "нет фото",
    skip_missing_category_mapping: "нет маппинга категории",
    skip_missing_producer: "нет маппинга бренда (обязателен для категории)",
    unmapped_attribute_values: "значений характеристик без маппинга (товар не пропущен)",
    params_truncated: "товаров с обрезанными характеристиками (лимит 100)",
};

const statusLabel = (status) => {
    switch (status) {
        case "success":
            return "OK";
        case "partial":
            return "частично";
        case "failed":
            return "ошибка";
        case "running":
            return "выполняется";
        default:
            return status;
    }
};

const RozetkaIntegrationPage = () => {
    const location = useLocation();
    const [section, setSection] = useState(() => new URLSearchParams(location.search).get("section") || "account");
    const [accounts, setAccounts] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState(() => new URLSearchParams(location.search).get("account") || "");
    const rozetkaPricingMode = useMarketplacePricingMode("rozetka", selectedAccountId);
    const [draft, setDraft] = useState(emptyRozetkaAccount);
    const [message, setMessage] = useState("");
    const [saving, setSaving] = useState(false);
    const [healthcheckResult, setHealthcheckResult] = useState(null);

    // --- Отправители/цены ---

    // --- Выгрузка ---
    const [feedResult, setFeedResult] = useState(null);
    const [productCode, setProductCode] = useState("");
    const [catalogOpResult, setCatalogOpResult] = useState(null);

    // --- Jobs ---
    const [jobs, setJobs] = useState([]);
    const [priceQuarantine, setPriceQuarantine] = useState([]);

    // --- Заказы (Этап 2, Фаза 3) ---
    const [businessStores, setBusinessStores] = useState([]);
    const [enterprises, setEnterprises] = useState([]);
    const [ordersHealthcheck, setOrdersHealthcheck] = useState(null);
    const [ordersDryRun, setOrdersDryRun] = useState(null);
    const [orders, setOrders] = useState([]);

    // --- Виды оплат ---
    const [paymentMappings, setPaymentMappings] = useState([]);
    const [paymentMappingDraft, setPaymentMappingDraft] = useState(emptyPaymentMappingDraft);
    const [editingPaymentMappingId, setEditingPaymentMappingId] = useState(null);
    const [rozetkaPaymentMethods, setRozetkaPaymentMethods] = useState([]);
    const [salesdrivePaymentMethods, setSalesdrivePaymentMethods] = useState([]);
    const [sdPaymentMethodsDirectory, setSdPaymentMethodsDirectory] = useState([]);
    const [sdPaymentMethodDraft, setSdPaymentMethodDraft] = useState(emptySdPaymentMethodDraft);
    const [editingSdPaymentMethodId, setEditingSdPaymentMethodId] = useState(null);

    // --- Статусы + ттн исходящие ---
    const [statusMappings, setStatusMappings] = useState([]);
    const [statusMappingDraft, setStatusMappingDraft] = useState(emptyStatusMappingDraft);
    const [editingStatusMappingId, setEditingStatusMappingId] = useState(null);
    const [rozetkaOrderStatuses, setRozetkaOrderStatuses] = useState([]);
    const [salesdriveStatuses, setSalesdriveStatuses] = useState([]);

    const selectedAccount = accounts.find((row) => String(row.id) === String(selectedAccountId));

    const loadAccounts = async (preferId) => {
        const rows = await getRozetkaAccounts();
        setAccounts(rows || []);
        const preferred = preferId ?? selectedAccountId;
        const stillExists = rows?.some((row) => String(row.id) === String(preferred));
        setSelectedAccountId(stillExists ? String(preferred) : rows?.[0] ? String(rows[0].id) : "");
    };

    useEffect(() => {
        loadAccounts(null).catch(() => setMessage("Не удалось загрузить аккаунты Rozetka."));
        getBusinessOrganizations()
            .then((rows) => setOrganizations(rows || []))
            .catch(() => {});
        getBusinessStores()
            .then((rows) => setBusinessStores(Array.isArray(rows) ? rows : []))
            .catch(() => setBusinessStores([]));
        getEnterprises()
            .then((rows) => setEnterprises(Array.isArray(rows) ? rows : []))
            .catch(() => setEnterprises([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const selected = accounts.find((row) => String(row.id) === String(selectedAccountId));
        setDraft({ ...emptyRozetkaAccount, ...(selected || {}), api_password: "", api_token: "" });
        setHealthcheckResult(null);
    }, [selectedAccountId, accounts]);

    const loadJobs = useCallback(async () => {
        if (!selectedAccountId) return;
        setJobs((await getRozetkaJobs(Number(selectedAccountId))) || []);
    }, [selectedAccountId]);

    const loadPriceQuarantine = useCallback(async () => {
        if (!selectedAccountId) return;
        try {
            setPriceQuarantine((await getRozetkaPriceQuarantine(Number(selectedAccountId))) || []);
        } catch (error) {
            // Ценовой предохранитель -- диагностика; не блокируем вкладку Jobs, если Rozetka API недоступен.
            setPriceQuarantine([]);
        }
    }, [selectedAccountId]);

    const loadPaymentMappings = useCallback(async () => {
        if (!selectedAccountId) return;
        setPaymentMappings((await getRozetkaPaymentMappings(Number(selectedAccountId))) || []);
    }, [selectedAccountId]);

    const loadOrders = useCallback(async () => {
        if (!selectedAccountId) return;
        setOrders((await getRozetkaOrders(Number(selectedAccountId))) || []);
    }, [selectedAccountId]);

    const loadStatusMappings = useCallback(async () => {
        if (!selectedAccountId) return;
        setStatusMappings((await getRozetkaOrderStatusMappings(Number(selectedAccountId))) || []);
    }, [selectedAccountId]);

    // Справочники для выпадающих списков -- живой Rozetka API может быть
    // недоступен (нет токена / нет прав на группу методов), поэтому ошибки
    // тут не блокируют вкладку, просто оставляют список пустым (останется
    // текстовый ввод как запасной вариант — см. рендер полей ниже).
    const loadPaymentReferenceLists = useCallback(async () => {
        if (!selectedAccountId) return;
        try {
            setRozetkaPaymentMethods((await getRozetkaPaymentMethods(Number(selectedAccountId))) || []);
        } catch (error) {
            setRozetkaPaymentMethods([]);
        }
        try {
            setSalesdrivePaymentMethods((await getRozetkaSalesdrivePaymentMethods(Number(selectedAccountId))) || []);
        } catch (error) {
            setSalesdrivePaymentMethods([]);
        }
    }, [selectedAccountId]);

    // Справочник способов оплаты SalesDrive (имя+код) — не привязан к аккаунту,
    // ведётся вручную оператором (нет живого API у SalesDrive). Источник для
    // дропдауна выше (только активные, через loadPaymentReferenceLists) и для
    // собственной CRUD-таблицы на этой же вкладке.
    const loadSdPaymentMethodsDirectory = useCallback(async () => {
        try {
            setSdPaymentMethodsDirectory((await getSalesdrivePaymentMethodsDirectory()) || []);
        } catch (error) {
            setSdPaymentMethodsDirectory([]);
        }
    }, []);

    const loadStatusReferenceLists = useCallback(async () => {
        if (!selectedAccountId) return;
        try {
            setRozetkaOrderStatuses((await getRozetkaOrderStatuses(Number(selectedAccountId))) || []);
        } catch (error) {
            setRozetkaOrderStatuses([]);
        }
        try {
            setSalesdriveStatuses((await getRozetkaSalesdriveStatuses(Number(selectedAccountId))) || []);
        } catch (error) {
            setSalesdriveStatuses([]);
        }
    }, [selectedAccountId]);

    useEffect(() => {
        if (section === "jobs") loadPriceQuarantine();
        if (section === "payments") {
            loadPaymentMappings().catch(() => setMessage("Не удалось загрузить виды оплат."));
            loadPaymentReferenceLists();
            loadSdPaymentMethodsDirectory();
        }
        if (section === "orders") loadOrders().catch(() => setMessage("Не удалось загрузить заказы."));
        if (section === "statuses") {
            loadStatusMappings().catch(() => setMessage("Не удалось загрузить маппинг статусов."));
            loadStatusReferenceLists();
        }
    }, [
        section,
        loadPriceQuarantine,
        loadPaymentMappings,
        loadPaymentReferenceLists,
        loadSdPaymentMethodsDirectory,
        loadOrders,
        loadStatusMappings,
        loadStatusReferenceLists,
    ]);

    useEffect(() => {
        setOrdersHealthcheck(null);
        setOrdersDryRun(null);
        setPaymentMappingDraft(emptyPaymentMappingDraft);
        setEditingPaymentMappingId(null);
        setStatusMappingDraft(emptyStatusMappingDraft);
        setEditingStatusMappingId(null);
    }, [selectedAccountId]);

    // Jobs грузим независимо от активной вкладки -- вкладке "Выгрузка" нужна
    // ссылка на фид из последнего успешного job'а, чтобы не терять её при
    // обновлении страницы (feedResult -- это только текущая сессия в браузере).
    useEffect(() => {
        loadJobs().catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить jobs."));
    }, [loadJobs]);

    const latestFeedJob = jobs.find((job) => job.job_type === "feed_generate" && job.response_payload?.feed_public_url) || null;
    const latestFeedUrlRaw = latestFeedJob?.response_payload?.feed_public_url;
    const latestFeedUrl = latestFeedUrlRaw?.startsWith("/") ? `${window.location.origin}${latestFeedUrlRaw}` : latestFeedUrlRaw;

    const copyLatestFeedUrl = async () => {
        if (!latestFeedUrl) return;
        try {
            await navigator.clipboard.writeText(latestFeedUrl);
            setMessage("Ссылка на фид скопирована.");
        } catch (error) {
            setMessage("Не удалось скопировать — скопируйте вручную.");
        }
    };

    const saveAccount = async () => {
        setSaving(true);
        setMessage("");
        try {
            const payload = { ...draft };
            delete payload.id;
            delete payload.platform;
            delete payload.api_password_set;
            delete payload.api_token_set;
            delete payload.created_at;
            delete payload.updated_at;
            delete payload.token_expires_at;
            delete payload.receipt_link_sync_started_at;
            delete payload.feed_last_manual_sync_at;
            delete payload.last_catalog_run_at;
            delete payload.last_stock_price_run_at;
            delete payload.last_order_fetch_at;
            delete payload.last_order_success_at;
            delete payload.last_order_error;
            if (!payload.api_password) delete payload.api_password;
            if (!payload.api_token) delete payload.api_token;
            payload.business_organization_id = payload.business_organization_id || null;
            payload.business_store_id = payload.business_store_id === "" ? null : Number(payload.business_store_id);
            payload.salesdrive_api_enterprise_code = payload.salesdrive_api_enterprise_code || null;

            const saved = selectedAccountId
                ? await updateRozetkaAccount(Number(selectedAccountId), payload)
                : await createRozetkaAccount(payload);
            setMessage("Аккаунт сохранён.");
            await loadAccounts(saved.id);
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось сохранить аккаунт.");
        } finally {
            setSaving(false);
        }
    };

    const runHealthcheck = async () => {
        if (!selectedAccountId) return;
        setMessage("");
        try {
            const result = await healthcheckRozetkaAccount(Number(selectedAccountId));
            setHealthcheckResult(result);
            setMessage("Healthcheck прошёл успешно.");
        } catch (error) {
            setHealthcheckResult(null);
            setMessage(error?.response?.data?.detail || "Healthcheck не прошёл.");
        }
    };

    const runGenerateFeed = async (dryRun) => {
        setMessage(dryRun ? "Dry-run генерации фида…" : "Генерация фида…");
        try {
            const result = await generateRozetkaFeed(Number(selectedAccountId), { dry_run: dryRun });
            setFeedResult(result);
            setMessage(`Фид: обработано ${result.processed}, пропущено ${result.skipped}, ошибок ${result.errors}.`);
        } catch (error) {
            if (error?.response?.status === 409 && error?.response?.data?.detail?.code === "feed_generation_in_progress") {
                const jobId = error.response.data.detail.job_id;
                await loadJobs();
                setMessage(`Фид уже генерируется${jobId ? `: job ${jobId}` : ""}. Дождитесь завершения текущей выгрузки.`);
                return;
            }
            setMessage(error?.response?.data?.detail || "Не удалось сгенерировать фид.");
        }
    };

    const runCreateProduct = async () => {
        if (!productCode.trim()) return;
        try {
            const result = await createRozetkaProduct(Number(selectedAccountId), productCode.trim());
            setCatalogOpResult(result);
            setMessage(result.upload_status === "created" ? `Товар ${productCode} создан на Rozetka.` : `Не удалось создать: ${result.error || "unknown"}`);
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось создать товар.");
        }
    };

    const runUpdateProductContent = async () => {
        if (!productCode.trim()) return;
        try {
            const result = await updateRozetkaProductContent(Number(selectedAccountId), productCode.trim());
            setCatalogOpResult(result);
            setMessage(result.upload_status === "updated" ? `Контент товара ${productCode} обновлён.` : `Не удалось обновить: ${result.error || "unknown"}`);
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось обновить контент товара.");
        }
    };

    const runSyncModeration = async () => {
        try {
            const result = await syncRozetkaModerationStatus(Number(selectedAccountId));
            setMessage(`Статусы модерации обновлены: ${result.updated}.`);
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось обновить статусы модерации.");
        }
    };

    const runSyncLinks = async () => {
        setMessage("Синхронизация связок товаров…");
        try {
            const result = await syncRozetkaProductLinks(Number(selectedAccountId));
            setMessage(`Связок синхронизировано: ${result.processed}.`);
            await loadJobs();
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось синхронизировать связки.");
        }
    };

    const runSyncStockPrices = async () => {
        setMessage("Синхронизация остатков/цен…");
        try {
            const result = await syncRozetkaStockPrices(Number(selectedAccountId));
            setMessage(`Остатки/цены: обработано ${result.processed}, пропущено ${result.skipped}, ошибок ${result.errors}.`);
            await loadJobs();
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось синхронизировать остатки/цены.");
        }
    };

    const runRepairMainImages = async (dryRun) => {
        if (!dryRun && !window.confirm("Обновить все связанные карточки Rozetka текущим контентом и одним главным фото?")) return;
        setMessage(dryRun ? "Проверка исправления главных фото…" : "Исправление главных фото…");
        try {
            const result = await repairRozetkaMainImages(Number(selectedAccountId), { dry_run: dryRun });
            setMessage(`Главные фото: обработано ${result.processed}, пропущено ${result.skipped}, ошибок ${result.errors}.`);
            await loadJobs();
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось выполнить исправление главных фото.");
        }
    };

    const downloadSkippedReport = async (jobId) => {
        try {
            await downloadRozetkaSkippedReport(Number(selectedAccountId), jobId);
        } catch (error) {
            let detail = error?.response?.data?.detail;
            if (!detail && error?.response?.data instanceof Blob) {
                try {
                    const payload = JSON.parse(await error.response.data.text());
                    detail = payload?.detail;
                } catch (_parseError) {
                    // Keep the generic message when the server did not return JSON.
                }
            }
            setMessage(detail || "Отчёт по пропущенным для этого job недоступен.");
        }
    };

    const runOrdersHealthcheck = async () => {
        setMessage("Проверка доступа к заказам Rozetka…");
        try {
            const result = await healthcheckRozetkaOrders(Number(selectedAccountId));
            setOrdersHealthcheck(result);
            setMessage(`Healthcheck заказов OK: получено ${result.fetched}.`);
        } catch (error) {
            setOrdersHealthcheck(null);
            setMessage(error?.response?.data?.detail || "Healthcheck заказов не прошёл.");
        }
    };

    const runOrdersDryRun = async () => {
        setMessage("Dry-run забор заказов (без записи и отправки)…");
        try {
            const result = await dryRunRozetkaOrders(Number(selectedAccountId), 10);
            setOrdersDryRun(result);
            setMessage(`Dry-run: fetched=${result.fetched}, mapping_errors=${result.mapping_errors}${result.reason ? `, reason=${result.reason}` : ""}.`);
        } catch (error) {
            setOrdersDryRun(null);
            setMessage(error?.response?.data?.detail || "Не удалось выполнить dry-run.");
        }
    };

    const startEditPaymentMapping = (row) => {
        setEditingPaymentMappingId(row.id);
        setPaymentMappingDraft({
            external_payment_method_id: row.external_payment_method_id,
            external_payment_method_name: row.external_payment_method_name || "",
            salesdrive_handler_value: row.salesdrive_handler_value || "",
            initial_status_id: row.initial_status_id ?? "",
            has_postpay: !!row.has_postpay,
            payment_policy: row.payment_policy || "manual_review",
            is_active: !!row.is_active,
            notes: row.notes || "",
        });
    };

    const cancelEditPaymentMapping = () => {
        setEditingPaymentMappingId(null);
        setPaymentMappingDraft(emptyPaymentMappingDraft);
    };

    const savePaymentMapping = async () => {
        if (!paymentMappingDraft.external_payment_method_id.trim() && !editingPaymentMappingId) {
            setMessage("ID вида оплаты Rozetka обязателен.");
            return;
        }
        try {
            const payload = {
                ...paymentMappingDraft,
                initial_status_id: paymentMappingDraft.initial_status_id === "" ? null : Number(paymentMappingDraft.initial_status_id),
            };
            if (editingPaymentMappingId) {
                delete payload.external_payment_method_id;
                await updateRozetkaPaymentMapping(Number(selectedAccountId), editingPaymentMappingId, payload);
                setMessage("Маппинг оплаты обновлён.");
            } else {
                await createRozetkaPaymentMapping(Number(selectedAccountId), payload);
                setMessage("Маппинг оплаты добавлен.");
            }
            cancelEditPaymentMapping();
            await loadPaymentMappings();
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось сохранить маппинг оплаты.");
        }
    };

    const deletePaymentMappingRow = async (row) => {
        try {
            await deleteRozetkaPaymentMapping(Number(selectedAccountId), row.id);
            setMessage(`Маппинг «${row.external_payment_method_id}» удалён.`);
            await loadPaymentMappings();
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось удалить маппинг оплаты.");
        }
    };

    const startEditSdPaymentMethod = (row) => {
        setEditingSdPaymentMethodId(row.id);
        setSdPaymentMethodDraft({ name: row.name, code: row.code, is_active: !!row.is_active });
    };

    const cancelEditSdPaymentMethod = () => {
        setEditingSdPaymentMethodId(null);
        setSdPaymentMethodDraft(emptySdPaymentMethodDraft);
    };

    const saveSdPaymentMethod = async () => {
        if (!sdPaymentMethodDraft.name.trim() || !sdPaymentMethodDraft.code.trim()) {
            setMessage("Название и код способа оплаты SalesDrive обязательны.");
            return;
        }
        try {
            if (editingSdPaymentMethodId) {
                await updateSalesdrivePaymentMethod(editingSdPaymentMethodId, sdPaymentMethodDraft);
                setMessage("Способ оплаты SalesDrive обновлён.");
            } else {
                await createSalesdrivePaymentMethod(sdPaymentMethodDraft);
                setMessage("Способ оплаты SalesDrive добавлен.");
            }
            cancelEditSdPaymentMethod();
            await loadSdPaymentMethodsDirectory();
            await loadPaymentReferenceLists();
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось сохранить способ оплаты SalesDrive.");
        }
    };

    const deleteSdPaymentMethodRow = async (row) => {
        try {
            await deleteSalesdrivePaymentMethod(row.id);
            setMessage(`Способ оплаты «${row.name}» удалён.`);
            await loadSdPaymentMethodsDirectory();
            await loadPaymentReferenceLists();
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось удалить способ оплаты SalesDrive.");
        }
    };

    const startEditStatusMapping = (row) => {
        setEditingStatusMappingId(row.id);
        setStatusMappingDraft({
            source_status_id: row.source_status_id,
            source_status_name: row.source_status_name || "",
            target_action: row.target_action,
            target_status: row.target_status || "",
            requires_ttn: !!row.requires_ttn,
            is_active: !!row.is_active,
            notes: row.notes || "",
        });
    };

    const cancelEditStatusMapping = () => {
        setEditingStatusMappingId(null);
        setStatusMappingDraft(emptyStatusMappingDraft);
    };

    const saveStatusMapping = async () => {
        if (!statusMappingDraft.source_status_id && !editingStatusMappingId) {
            setMessage("SalesDrive statusId обязателен.");
            return;
        }
        try {
            const payload = { ...statusMappingDraft };
            if (editingStatusMappingId) {
                delete payload.source_status_id;
                await updateRozetkaOrderStatusMapping(Number(selectedAccountId), editingStatusMappingId, payload);
                setMessage("Маппинг статуса обновлён.");
            } else {
                payload.source_status_id = Number(payload.source_status_id);
                await createRozetkaOrderStatusMapping(Number(selectedAccountId), payload);
                setMessage("Маппинг статуса добавлен.");
            }
            cancelEditStatusMapping();
            await loadStatusMappings();
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось сохранить маппинг статуса.");
        }
    };

    const deleteStatusMappingRow = async (row) => {
        try {
            await deleteRozetkaOrderStatusMapping(Number(selectedAccountId), row.id);
            setMessage(`Маппинг statusId=${row.source_status_id} удалён.`);
            await loadStatusMappings();
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось удалить маппинг статуса.");
        }
    };

    return (
        <div className="rozetka-page" style={{ display: "grid", gap: "16px" }}>
            <RozetkaTopBar
                activeKey={section}
                accountId={selectedAccountId}
                localKeys={["account", "suppliers", "pricing", "export", "jobs", "orders", "payments", "statuses"]}
                onLocalSelect={setSection}
            />
            {message && <div className="rzm-status">{message}</div>}

            {section === "account" && (
                <section className="rzm-card">
                    <Field label="Аккаунт">
                        <select
                            className="rzm-inp"
                            style={{ maxWidth: "420px" }}
                            value={selectedAccountId}
                            onChange={(event) => setSelectedAccountId(event.target.value)}
                        >
                            <option value="">— новый аккаунт —</option>
                            {accounts.map((row) => (
                                <option key={row.id} value={row.id}>
                                    {row.account_name} ({row.account_code})
                                </option>
                            ))}
                        </select>
                    </Field>
                    <div className="rzm-grid-auto">
                        <Field label="Код аккаунта">
                            <input className="rzm-inp" value={draft.account_code} onChange={(event) => setDraft({ ...draft, account_code: event.target.value })} />
                        </Field>
                        <Field label="Название">
                            <input className="rzm-inp" value={draft.account_name} onChange={(event) => setDraft({ ...draft, account_name: event.target.value })} />
                        </Field>
                        <Field label="Юрлицо / организация">
                            <select
                                className="rzm-inp"
                                value={draft.business_organization_id || ""}
                                onChange={(event) => setDraft({ ...draft, business_organization_id: event.target.value })}
                            >
                                <option value="">— не выбрано —</option>
                                {organizations.map((org) => (
                                    <option key={org.id} value={org.id}>
                                        {org.short_name || org.name || `ID ${org.id}`}
                                        {org.tax_id ? ` / ${org.tax_id}` : ""}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Base URL">
                            <input className="rzm-inp" value={draft.base_url} onChange={(event) => setDraft({ ...draft, base_url: event.target.value })} />
                        </Field>
                        <Field label="Логин">
                            <input className="rzm-inp" value={draft.api_login || ""} onChange={(event) => setDraft({ ...draft, api_login: event.target.value })} />
                        </Field>
                        <Field label={draft.api_password_set ? "Пароль (сохранён — оставьте пустым, чтобы не менять)" : "Пароль"}>
                            <input
                                className="rzm-inp"
                                type="password"
                                value={draft.api_password || ""}
                                onChange={(event) => setDraft({ ...draft, api_password: event.target.value })}
                            />
                        </Field>
                        <Field label={draft.api_token_set ? "API-токен Rozetka (сохранён — оставьте пустым, чтобы не менять)" : "API-токен Rozetka"}>
                            <input
                                className="rzm-inp"
                                type="password"
                                autoComplete="new-password"
                                value={draft.api_token || ""}
                                onChange={(event) => setDraft({ ...draft, api_token: event.target.value })}
                                placeholder="Bearer-токен, выданный Rozetka"
                            />
                        </Field>
                        <Field label="Язык фида">
                            <select className="rzm-inp" value={draft.default_language} onChange={(event) => setDraft({ ...draft, default_language: event.target.value })}>
                                <option value="uk">uk</option>
                                <option value="ru">ru</option>
                            </select>
                        </Field>
                        <Field label="Источник цены/остатка (тип синхронізації в кабинете Rozetka)">
                            <select
                                className="rzm-inp"
                                value={draft.feed_stock_source}
                                onChange={(event) => setDraft({ ...draft, feed_stock_source: event.target.value })}
                            >
                                <option value="pricelist">прайс-лист (фид)</option>
                                <option value="api">особистий кабінет (API)</option>
                            </select>
                        </Field>
                        <Field label="Лимит товаров в фиде">
                            <input
                                className="rzm-inp"
                                type="number"
                                min={1}
                                max={100000}
                                value={draft.feed_product_limit}
                                onChange={(event) => setDraft({ ...draft, feed_product_limit: Number(event.target.value) })}
                            />
                        </Field>
                        <Field label="Лимит сканирования связей (goods/*)">
                            <input
                                className="rzm-inp"
                                type="number"
                                min={100}
                                max={10000}
                                value={draft.product_link_scan_limit}
                                onChange={(event) => setDraft({ ...draft, product_link_scan_limit: Number(event.target.value) })}
                            />
                        </Field>
                        <Field label="Интервал генерации фида, сек">
                            <input
                                className="rzm-inp"
                                type="number"
                                min={300}
                                value={draft.catalog_schedule_interval_sec}
                                onChange={(event) => setDraft({ ...draft, catalog_schedule_interval_sec: Number(event.target.value) })}
                            />
                        </Field>
                        <Field label="Интервал синхронизации остатков/цен, сек">
                            <input
                                className="rzm-inp"
                                type="number"
                                min={300}
                                value={draft.stock_price_schedule_interval_sec}
                                onChange={(event) => setDraft({ ...draft, stock_price_schedule_interval_sec: Number(event.target.value) })}
                            />
                        </Field>
                    </div>
                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                        <CheckboxField label="Активен" checked={!!draft.is_active} onChange={(v) => setDraft({ ...draft, is_active: v })} />
                        <CheckboxField label="Каталог включён" checked={!!draft.catalog_enabled} onChange={(v) => setDraft({ ...draft, catalog_enabled: v })} />
                        <CheckboxField
                            label="Остатки/цены включены"
                            checked={!!draft.stock_price_enabled}
                            onChange={(v) => setDraft({ ...draft, stock_price_enabled: v })}
                        />
                        <CheckboxField
                            label="Игнорировать ценовой предохранитель"
                            checked={!!draft.price_guard_ignore_check}
                            onChange={(v) => setDraft({ ...draft, price_guard_ignore_check: v })}
                        />
                    </div>
                    <Field label="Заметки">
                        <textarea className="rzm-inp" value={draft.notes || ""} onChange={(event) => setDraft({ ...draft, notes: event.target.value })} />
                    </Field>
                    <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                        <button type="button" className="rzm-btn rzm-btn-primary" disabled={saving || !draft.account_code || !draft.account_name} onClick={saveAccount}>
                            {saving ? "Сохраняем…" : "Сохранить"}
                        </button>
                        <button type="button" className="rzm-btn" disabled={!selectedAccountId} onClick={runHealthcheck}>
                            Healthcheck
                        </button>
                    </div>
                    {healthcheckResult && <pre className="rzm-pre">{JSON.stringify(healthcheckResult, null, 2)}</pre>}
                </section>
            )}

            {section === "pricing" && (
                <MarketplacePricingTab
                    platform="rozetka"
                    accountId={selectedAccountId}
                    onModeChange={rozetkaPricingMode.reload}
                />
            )}

            {section === "suppliers" && (
                <MarketplaceSendersTable
                    platform="rozetka"
                    accountId={selectedAccountId}
                    defaultProfit={rozetkaPricingMode.defaultProfit}
                />
            )}

            {section === "export" && !selectedAccountId && (
                <section className="rzm-card">
                    <p className="rzm-warn">Сначала создайте и сохраните аккаунт на вкладке «Аккаунт».</p>
                </section>
            )}

            {section === "export" && selectedAccountId && (
                <>
                    <section className="rzm-card">
                        <h3>Фид контента</h3>
                        {selectedAccount?.feed_stock_source !== "api" && (
                            <p className="rzm-warn">
                                Внимание: feed_stock_source = «{selectedAccount?.feed_stock_source}» — обновления остатков/цен через API будут
                                проигнорированы Rozetka, пока источник в кабинете не переключён на «Особистий кабінет (API)».
                            </p>
                        )}
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            <button type="button" className="rzm-btn rzm-btn-primary" onClick={() => runGenerateFeed(false)}>
                                Сгенерировать фид
                            </button>
                            <button type="button" className="rzm-btn" onClick={() => runGenerateFeed(true)}>
                                Dry-run
                            </button>
                        </div>
                        {latestFeedUrl ? (
                            <div>
                                <p className="rzm-hint" style={{ margin: 0 }}>
                                    Постоянная ссылка на фид (последний успешный запуск: job #{latestFeedJob.id},{" "}
                                    {latestFeedJob.started_at ? new Date(latestFeedJob.started_at).toLocaleString() : "—"}) — не пропадает
                                    при обновлении страницы, эту ссылку можно отдать менеджеру Rozetka:
                                </p>
                                <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginTop: "6px" }}>
                                    <code style={{ wordBreak: "break-all" }}>{latestFeedUrl}</code>
                                    <button type="button" className="rzm-btn rzm-btn-sm" onClick={copyLatestFeedUrl}>
                                        Копировать
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <p className="rzm-hint">Ссылка появится здесь после первой успешной (не dry-run) генерации фида.</p>
                        )}
                        {feedResult && (
                            <div>
                                <p style={{ margin: 0 }}>
                                    Job #{feedResult.job_id} · {statusLabel(feedResult.status)} · обработано {feedResult.processed}, пропущено{" "}
                                    {feedResult.skipped}, ошибок {feedResult.errors}
                                </p>
                                {feedResult.feed_public_url && (
                                    <p style={{ margin: 0 }}>
                                        URL:{" "}
                                        <a href={feedResult.feed_public_url} target="_blank" rel="noreferrer">
                                            {feedResult.feed_public_url}
                                        </a>
                                    </p>
                                )}
                                {feedResult.diagnostics && Object.keys(feedResult.diagnostics).length > 0 && (
                                    <div style={{ marginTop: "8px" }}>
                                        <p style={{ margin: 0, fontWeight: 700 }}>Причины:</p>
                                        <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
                                            {Object.entries(feedResult.diagnostics).map(([key, count]) => (
                                                <li key={key} style={{ color: "var(--rzm-text-2)" }}>
                                                    {DIAGNOSTIC_LABELS[key] || key}: <strong>{count}</strong>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                    <section className="rzm-card">
                        <h3>Точечный API каталога</h3>
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(200px,1fr) auto auto auto", gap: "8px", alignItems: "end" }}>
                            <Field label="Код товара (SKU)">
                                <input className="rzm-inp" value={productCode} onChange={(event) => setProductCode(event.target.value)} />
                            </Field>
                            <button type="button" className="rzm-btn" onClick={runCreateProduct}>
                                Создать на Rozetka
                            </button>
                            <button type="button" className="rzm-btn" onClick={runUpdateProductContent}>
                                Обновить контент
                            </button>
                            <button type="button" className="rzm-btn" onClick={runSyncModeration}>
                                Обновить статусы модерации
                            </button>
                        </div>
                        {catalogOpResult && <pre className="rzm-pre">{JSON.stringify(catalogOpResult, null, 2)}</pre>}
                    </section>
                </>
            )}

            {section === "jobs" && !selectedAccountId && (
                <section className="rzm-card">
                    <p className="rzm-warn">Сначала создайте и сохраните аккаунт на вкладке «Аккаунт».</p>
                </section>
            )}

            {section === "jobs" && selectedAccountId && (
                <>
                    <section className="rzm-card">
                        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
                            <h3>Операции Rozetka</h3>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button type="button" className="rzm-btn" onClick={() => runRepairMainImages(true)}>
                                    Проверить главные фото
                                </button>
                                <button type="button" className="rzm-btn rzm-btn-primary" onClick={() => runRepairMainImages(false)}>
                                    Исправить все карточки
                                </button>
                                <button type="button" className="rzm-btn" onClick={runSyncLinks}>
                                    Синхронизировать связки
                                </button>
                                <button type="button" className="rzm-btn rzm-btn-primary" onClick={runSyncStockPrices}>
                                    Синхронизировать остатки/цены
                                </button>
                            </div>
                        </div>
                        {priceQuarantine.length > 0 && (
                            <div>
                                <p className="rzm-warn">В ценовом карантине: {priceQuarantine.length}</p>
                                <table className="rzm-table">
                                    <thead>
                                        <tr>
                                            <th>SKU</th>
                                            <th>Текущая цена</th>
                                            <th>Ожидает подтверждения</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {priceQuarantine.map((item, index) => (
                                            <tr key={item.external_id || index}>
                                                <td>{item.external_id}</td>
                                                <td>{item.current_price}</td>
                                                <td>{item.pending_price}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                    <section className="rzm-card">
                        <h3>История job'ов ({jobs.length})</h3>
                        <table className="rzm-table">
                            <thead>
                                <tr>
                                    <th>Тип</th>
                                    <th>Статус</th>
                                    <th>Всего/обработано/пропущено/ошибок</th>
                                    <th>Начат</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {jobs.map((job) => (
                                    <tr key={job.id}>
                                        <td>{jobTypeLabel(job.job_type)}</td>
                                        <td>{statusLabel(job.status)}</td>
                                        <td>
                                            {job.total}/{job.processed}/{job.skipped}/{job.errors}
                                        </td>
                                        <td>{job.started_at ? new Date(job.started_at).toLocaleString() : "—"}</td>
                                        <td>
                                            {job.skipped > 0 && job.response_payload?.skipped_report_path && (
                                                <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => downloadSkippedReport(job.id)}>
                                                    Отчёт по пропущенным
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                </>
            )}

            {section === "orders" && !selectedAccountId && (
                <section className="rzm-card">
                    <p className="rzm-warn">Сначала создайте и сохраните аккаунт на вкладке «Аккаунт».</p>
                </section>
            )}

            {section === "orders" && selectedAccountId && (
                <>
                    <section className="rzm-card">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3>Заказы Rozetka → SalesDrive</h3>
                            <HelpTip section="orders" />
                        </div>
                        <div className="rzm-grid-auto">
                            <Field label="BusinessStore (offer scope)">
                                <select
                                    className="rzm-inp"
                                    value={String(draft.business_store_id ?? "")}
                                    onChange={(event) => setDraft({ ...draft, business_store_id: event.target.value })}
                                >
                                    <option value="">— не выбрано —</option>
                                    {businessStores.map((store) => (
                                        <option key={store.id} value={String(store.id)}>
                                            {store.store_code} — {store.store_name || store.salesdrive_store_name || ""}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="SalesDrive enterprise_code (источник API-ключа)">
                                <select
                                    className="rzm-inp"
                                    value={draft.salesdrive_api_enterprise_code || ""}
                                    onChange={(event) => setDraft({ ...draft, salesdrive_api_enterprise_code: event.target.value })}
                                >
                                    <option value="">— не выбрано —</option>
                                    {enterprises.map((ent) => (
                                        <option key={ent.enterprise_code} value={ent.enterprise_code}>
                                            {(ent.enterprise_name || ent.enterprise_code)} ({ent.enterprise_code}){ent.token ? "" : " — без токена"}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Метка источника (sajt)">
                                <input
                                    className="rzm-inp"
                                    value={draft.salesdrive_source_label || ""}
                                    onChange={(event) => setDraft({ ...draft, salesdrive_source_label: event.target.value })}
                                />
                            </Field>
                            <Field label="Source-поля">
                                <select
                                    className="rzm-inp"
                                    value={draft.source_field_mode}
                                    onChange={(event) => setDraft({ ...draft, source_field_mode: event.target.value })}
                                >
                                    {SOURCE_MODES.map((mode) => (
                                        <option key={mode.value} value={mode.value}>
                                            {mode.label}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Интервал polling, сек">
                                <input
                                    className="rzm-inp"
                                    type="number"
                                    min={30}
                                    value={draft.order_fetch_interval_seconds}
                                    onChange={(event) => setDraft({ ...draft, order_fetch_interval_seconds: Number(event.target.value) })}
                                />
                            </Field>
                            <Field label="Lookback (дней, bootstrap)">
                                <input
                                    className="rzm-inp"
                                    type="number"
                                    min={1}
                                    value={draft.order_initial_lookback_days}
                                    onChange={(event) => setDraft({ ...draft, order_initial_lookback_days: Number(event.target.value) })}
                                />
                            </Field>
                            <Field label="Overlap, мин">
                                <input
                                    className="rzm-inp"
                                    type="number"
                                    min={0}
                                    value={draft.order_overlap_minutes}
                                    onChange={(event) => setDraft({ ...draft, order_overlap_minutes: Number(event.target.value) })}
                                />
                            </Field>
                        </div>
                        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                            <CheckboxField label="Polling заказов включён" checked={!!draft.orders_enabled} onChange={(v) => setDraft({ ...draft, orders_enabled: v })} />
                            <CheckboxField label="Live send (SalesDrive и Rozetka)" checked={!!draft.order_live_send_enabled} onChange={(v) => setDraft({ ...draft, order_live_send_enabled: v })} />
                            <CheckboxField
                                label="Создавать по отменённым"
                                checked={!!draft.create_canceled_orders}
                                onChange={(v) => setDraft({ ...draft, create_canceled_orders: v })}
                            />
                            <CheckboxField
                                label="Синк статусов оплаты (Фаза 5, кэш 5 мин)"
                                checked={!!draft.payment_status_sync_enabled}
                                onChange={(v) => setDraft({ ...draft, payment_status_sync_enabled: v })}
                            />
                        </div>
                        <p className="rzm-hint">
                            Гейтинг обработки по оплате (постоплата/предоплата) и отказ до отправки — встроены в общий бизнес-процессор,
                            отдельного флага не требуют (см. docs/rozetka_stage2_orders_ttn_plan.md §7).
                        </p>
                        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                            <button type="button" className="rzm-btn rzm-btn-primary" disabled={saving} onClick={saveAccount}>
                                {saving ? "Сохраняем…" : "Сохранить настройки заказов"}
                            </button>
                            <button type="button" className="rzm-btn" onClick={runOrdersHealthcheck}>
                                Healthcheck заказов
                            </button>
                            <button type="button" className="rzm-btn" onClick={runOrdersDryRun}>
                                Dry-run
                            </button>
                        </div>
                        {selectedAccount?.last_order_error && (
                            <p className="rzm-warn">Последняя ошибка polling-а: {selectedAccount.last_order_error}</p>
                        )}
                        {ordersHealthcheck && <pre className="rzm-pre">{JSON.stringify(ordersHealthcheck, null, 2)}</pre>}
                        {ordersDryRun && <pre className="rzm-pre">{JSON.stringify(ordersDryRun, null, 2)}</pre>}
                    </section>
                    {selectedAccount && <RozetkaReceipts
                        key={selectedAccount.id}
                        account={selectedAccount}
                        enabled={draft.receipt_link_sync_enabled}
                        onEnabledChange={(value) => setDraft({ ...draft, receipt_link_sync_enabled: value })}
                        onSave={saveAccount}
                        saving={saving}
                    />}
                    <section className="rzm-card">
                        <h3>Заказы ({orders.length})</h3>
                        <table className="rzm-table">
                            <thead>
                                <tr>
                                    <th>External ID</th>
                                    <th>Покупатель</th>
                                    <th>Статус Rozetka</th>
                                    <th>Нормализация</th>
                                    <th>SalesDrive</th>
                                    <th>Отправлено (статус/ттн)</th>
                                    <th>Оплата</th>
                                    <th>Поставщик</th>
                                    <th>Ошибка</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.external_order_number || row.external_order_id}</td>
                                        <td>{row.buyer_name}</td>
                                        <td>{row.external_status_name || row.external_status}</td>
                                        <td>{row.normalization_status}</td>
                                        <td>
                                            {row.local_status} / {row.salesdrive_status}
                                        </td>
                                        <td>
                                            {row.last_status_sent || "—"} ({row.status_sync_status || "—"}) /{" "}
                                            {row.last_ttn_sent || "—"} ({row.ttn_sync_status || "—"})
                                        </td>
                                        <td>
                                            {row.payment_status_name || row.payment_status_external || "—"}
                                            {row.payment_is_paid === true && " ✅"}
                                            {row.payment_is_paid === false && " ⏳"}
                                        </td>
                                        <td>{row.selected_supplier_code || row.supplier_selection_status}</td>
                                        <td style={{ color: "var(--rzm-danger)" }}>{row.last_error}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                </>
            )}

            {section === "payments" && !selectedAccountId && (
                <section className="rzm-card">
                    <p className="rzm-warn">Сначала создайте и сохраните аккаунт на вкладке «Аккаунт».</p>
                </section>
            )}

            {section === "payments" && selectedAccountId && (
                <>
                    <section className="rzm-card">
                        <h3>Справочник способов оплаты SalesDrive</h3>
                        <p className="rzm-hint">
                            В сопоставлениях Rozetka пока используется общий справочник имя+код. SalesDrive также
                            предоставляет <code>GET /api/payment-methods/</code>; Kasta загружает этот каталог напрямую.
                            «Код» — значение, которое уходит в SalesDrive полем <code>payment_method</code>.
                        </p>
                        <div className="rzm-grid-auto">
                            <Field label="Название">
                                <input
                                    className="rzm-inp"
                                    value={sdPaymentMethodDraft.name}
                                    onChange={(event) => setSdPaymentMethodDraft({ ...sdPaymentMethodDraft, name: event.target.value })}
                                />
                            </Field>
                            <Field label="Код (уходит в SalesDrive)">
                                <input
                                    className="rzm-inp"
                                    value={sdPaymentMethodDraft.code}
                                    onChange={(event) => setSdPaymentMethodDraft({ ...sdPaymentMethodDraft, code: event.target.value })}
                                />
                            </Field>
                        </div>
                        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                            <CheckboxField
                                label="Активен"
                                checked={!!sdPaymentMethodDraft.is_active}
                                onChange={(v) => setSdPaymentMethodDraft({ ...sdPaymentMethodDraft, is_active: v })}
                            />
                        </div>
                        <div style={{ display: "flex", gap: "10px" }}>
                            <button type="button" className="rzm-btn rzm-btn-primary" onClick={saveSdPaymentMethod}>
                                {editingSdPaymentMethodId ? "Сохранить изменения" : "Добавить способ оплаты"}
                            </button>
                            {editingSdPaymentMethodId && (
                                <button type="button" className="rzm-btn" onClick={cancelEditSdPaymentMethod}>
                                    Отмена
                                </button>
                            )}
                        </div>
                        <table className="rzm-table">
                            <thead>
                                <tr>
                                    <th>Название</th>
                                    <th>Код</th>
                                    <th>Активен</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {sdPaymentMethodsDirectory.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.name}</td>
                                        <td>{row.code}</td>
                                        <td>{row.is_active ? "да" : "нет"}</td>
                                        <td style={{ display: "flex", gap: "6px" }}>
                                            <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => startEditSdPaymentMethod(row)}>
                                                Изменить
                                            </button>
                                            <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => deleteSdPaymentMethodRow(row)}>
                                                Удалить
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                    <section className="rzm-card">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3>Виды оплат Rozetka → SalesDrive</h3>
                            <HelpTip section="payments" />
                        </div>
                        <div className="rzm-grid-auto">
                            <Field label="Вид оплаты Rozetka">
                                <select
                                    className="rzm-inp"
                                    disabled={!!editingPaymentMappingId}
                                    value={paymentMappingDraft.external_payment_method_id}
                                    onChange={(event) => {
                                        const id = event.target.value;
                                        const found = rozetkaPaymentMethods.find((opt) => String(opt.id) === String(id));
                                        setPaymentMappingDraft({
                                            ...paymentMappingDraft,
                                            external_payment_method_id: id,
                                            external_payment_method_name: found ? found.name : paymentMappingDraft.external_payment_method_name,
                                        });
                                    }}
                                >
                                    <option value="">— выбрать вид оплаты —</option>
                                    {rozetkaPaymentMethods.map((opt) => (
                                        <option key={opt.id} value={opt.id}>
                                            {opt.id} — {opt.name}
                                        </option>
                                    ))}
                                    {paymentMappingDraft.external_payment_method_id &&
                                        !rozetkaPaymentMethods.some(
                                            (opt) => String(opt.id) === String(paymentMappingDraft.external_payment_method_id)
                                        ) && (
                                            <option value={paymentMappingDraft.external_payment_method_id}>
                                                {paymentMappingDraft.external_payment_method_id}
                                                {paymentMappingDraft.external_payment_method_name
                                                    ? ` — ${paymentMappingDraft.external_payment_method_name}`
                                                    : " (нет в справочнике)"}
                                            </option>
                                        )}
                                </select>
                            </Field>
                            <Field label="SalesDrive способ оплаты">
                                <select
                                    className="rzm-inp"
                                    value={paymentMappingDraft.salesdrive_handler_value}
                                    onChange={(event) => setPaymentMappingDraft({ ...paymentMappingDraft, salesdrive_handler_value: event.target.value })}
                                >
                                    <option value="">— выбрать —</option>
                                    {salesdrivePaymentMethods.map((opt) => (
                                        <option key={opt.id} value={opt.code}>
                                            {opt.name} ({opt.code})
                                        </option>
                                    ))}
                                    {paymentMappingDraft.salesdrive_handler_value &&
                                        !salesdrivePaymentMethods.some((opt) => opt.code === paymentMappingDraft.salesdrive_handler_value) && (
                                            <option value={paymentMappingDraft.salesdrive_handler_value}>
                                                {paymentMappingDraft.salesdrive_handler_value} (нет в справочнике)
                                            </option>
                                        )}
                                </select>
                            </Field>
                            <Field label="Начальный statusId в SalesDrive">
                                <input
                                    className="rzm-inp"
                                    type="number"
                                    value={paymentMappingDraft.initial_status_id}
                                    onChange={(event) => setPaymentMappingDraft({ ...paymentMappingDraft, initial_status_id: event.target.value })}
                                />
                            </Field>
                            <Field label="Правило обработки оплаты">
                                <select
                                    className="rzm-inp"
                                    value={paymentMappingDraft.payment_policy}
                                    onChange={(event) => {
                                        const payment_policy = event.target.value;
                                        const defaults = {
                                            postpay: { has_postpay: true, initial_status_id: 1 },
                                            online_prepaid: { has_postpay: false, initial_status_id: 18 },
                                            manual_prepaid: { has_postpay: false, initial_status_id: 18 },
                                            manual_review: { has_postpay: false, initial_status_id: 18 },
                                        };
                                        setPaymentMappingDraft({ ...paymentMappingDraft, payment_policy, ...defaults[payment_policy] });
                                    }}
                                >
                                    <option value="postpay">Постоплата — обрабатывать сразу</option>
                                    <option value="online_prepaid">Онлайн-предоплата — ждать paid из Rozetka API</option>
                                    <option value="manual_prepaid">Ручная предоплата — ждать подтверждения менеджера</option>
                                    <option value="manual_review">Ручная проверка — не обрабатывать автоматически</option>
                                </select>
                            </Field>
                        </div>
                        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                            <CheckboxField
                                label="Постоплата (has_postpay) — обрабатывать сразу"
                                checked={!!paymentMappingDraft.has_postpay}
                                onChange={(v) => setPaymentMappingDraft({ ...paymentMappingDraft, has_postpay: v })}
                            />
                            <CheckboxField
                                label="Активен"
                                checked={!!paymentMappingDraft.is_active}
                                onChange={(v) => setPaymentMappingDraft({ ...paymentMappingDraft, is_active: v })}
                            />
                        </div>
                        <div style={{ display: "flex", gap: "10px" }}>
                            <button type="button" className="rzm-btn rzm-btn-primary" onClick={savePaymentMapping}>
                                {editingPaymentMappingId ? "Сохранить изменения" : "Добавить маппинг"}
                            </button>
                            {editingPaymentMappingId && (
                                <button type="button" className="rzm-btn" onClick={cancelEditPaymentMapping}>
                                    Отмена
                                </button>
                            )}
                        </div>
                        <table className="rzm-table">
                            <thead>
                                <tr>
                                    <th>ID Rozetka</th>
                                    <th>Название</th>
                                    <th>SalesDrive способ оплаты</th>
                                    <th>Постоплата</th>
                                    <th>Правило оплаты</th>
                                    <th>Активен</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {paymentMappings.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.external_payment_method_id}</td>
                                        <td>{row.external_payment_method_name}</td>
                                        <td>
                                            {sdPaymentMethodsDirectory.find((opt) => opt.code === row.salesdrive_handler_value)?.name ||
                                                row.salesdrive_handler_value ||
                                                "—"}
                                        </td>
                                        <td>{row.has_postpay ? "да" : "нет"}</td>
                                        <td>{{
                                            postpay: "Постоплата",
                                            online_prepaid: "Онлайн: ждать paid API",
                                            manual_prepaid: "Ручная предоплата",
                                            manual_review: "Ручная проверка",
                                        }[row.payment_policy] || "Ручная проверка"}</td>
                                        <td>{row.is_active ? "да" : "нет"}</td>
                                        <td style={{ display: "flex", gap: "6px" }}>
                                            <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => startEditPaymentMapping(row)}>
                                                Изменить
                                            </button>
                                            <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => deletePaymentMappingRow(row)}>
                                                Удалить
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                </>
            )}

            {section === "statuses" && !selectedAccountId && (
                <section className="rzm-card">
                    <p className="rzm-warn">Сначала создайте и сохраните аккаунт на вкладке «Аккаунт».</p>
                </section>
            )}

            {section === "statuses" && selectedAccountId && (
                <>
                    <section className="rzm-card">
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3>Маппинг статусов SalesDrive → Rozetka</h3>
                            <HelpTip section="statuses" />
                        </div>
                        <p className="rzm-hint">
                            Rozetka меняет статус+ттн+комментарий одним вызовом (PUT /orders/id). После отправки ттн
                            площадка сама продвигает статус доставки дальше — не заводите промежуточные переходы,
                            обычно достаточно одной строки на отмену (SalesDrive «Відмова» → статус 6).
                        </p>
                        <div className="rzm-grid-auto">
                            <Field label="Статус SalesDrive">
                                <select
                                    className="rzm-inp"
                                    disabled={!!editingStatusMappingId}
                                    value={statusMappingDraft.source_status_id}
                                    onChange={(event) => {
                                        const id = event.target.value;
                                        const found = salesdriveStatuses.find((opt) => String(opt.status_id) === String(id));
                                        setStatusMappingDraft({
                                            ...statusMappingDraft,
                                            source_status_id: id,
                                            source_status_name: found ? found.name : statusMappingDraft.source_status_name,
                                        });
                                    }}
                                >
                                    <option value="">— выбрать статус —</option>
                                    {salesdriveStatuses.map((opt) => (
                                        <option key={opt.status_id} value={opt.status_id}>
                                            {opt.status_id} — {opt.name}
                                            {opt.known ? "" : " (нет в справочнике)"}
                                        </option>
                                    ))}
                                    {statusMappingDraft.source_status_id !== "" &&
                                        !salesdriveStatuses.some(
                                            (opt) => String(opt.status_id) === String(statusMappingDraft.source_status_id)
                                        ) && (
                                            <option value={statusMappingDraft.source_status_id}>
                                                {statusMappingDraft.source_status_id}
                                                {statusMappingDraft.source_status_name ? ` — ${statusMappingDraft.source_status_name}` : " (нет в справочнике)"}
                                            </option>
                                        )}
                                </select>
                            </Field>
                            <Field label="Действие">
                                <select
                                    className="rzm-inp"
                                    value={statusMappingDraft.target_action}
                                    onChange={(event) => setStatusMappingDraft({ ...statusMappingDraft, target_action: event.target.value })}
                                >
                                    <option value="update_order">update_order (статус в Rozetka)</option>
                                    <option value="skip">skip (игнорировать)</option>
                                </select>
                            </Field>
                            <Field label="Целевой статус Rozetka">
                                <select
                                    className="rzm-inp"
                                    value={statusMappingDraft.target_status}
                                    onChange={(event) => setStatusMappingDraft({ ...statusMappingDraft, target_status: event.target.value })}
                                >
                                    <option value="">— выбрать статус —</option>
                                    {rozetkaOrderStatuses.map((opt) => (
                                        <option key={opt.id} value={opt.id}>
                                            {opt.id} — {opt.name}
                                        </option>
                                    ))}
                                    {statusMappingDraft.target_status &&
                                        !rozetkaOrderStatuses.some((opt) => String(opt.id) === String(statusMappingDraft.target_status)) && (
                                            <option value={statusMappingDraft.target_status}>
                                                {statusMappingDraft.target_status} (нет в справочнике)
                                            </option>
                                        )}
                                </select>
                            </Field>
                        </div>
                        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                            <CheckboxField
                                label="Требует ттн (не отправлять, пока ттн не известен)"
                                checked={!!statusMappingDraft.requires_ttn}
                                onChange={(v) => setStatusMappingDraft({ ...statusMappingDraft, requires_ttn: v })}
                            />
                            <CheckboxField
                                label="Активен"
                                checked={!!statusMappingDraft.is_active}
                                onChange={(v) => setStatusMappingDraft({ ...statusMappingDraft, is_active: v })}
                            />
                        </div>
                        <div style={{ display: "flex", gap: "10px" }}>
                            <button type="button" className="rzm-btn rzm-btn-primary" onClick={saveStatusMapping}>
                                {editingStatusMappingId ? "Сохранить изменения" : "Добавить маппинг"}
                            </button>
                            {editingStatusMappingId && (
                                <button type="button" className="rzm-btn" onClick={cancelEditStatusMapping}>
                                    Отмена
                                </button>
                            )}
                        </div>
                        <table className="rzm-table">
                            <thead>
                                <tr>
                                    <th>statusId SalesDrive</th>
                                    <th>Название</th>
                                    <th>Действие</th>
                                    <th>Статус Rozetka</th>
                                    <th>Требует ттн</th>
                                    <th>Активен</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {statusMappings.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.source_status_id}</td>
                                        <td>
                                            {salesdriveStatuses.find((opt) => String(opt.status_id) === String(row.source_status_id))?.name ||
                                                row.source_status_name ||
                                                "—"}
                                        </td>
                                        <td>{row.target_action}</td>
                                        <td>{row.target_status}</td>
                                        <td>{row.requires_ttn ? "да" : "нет"}</td>
                                        <td>{row.is_active ? "да" : "нет"}</td>
                                        <td style={{ display: "flex", gap: "6px" }}>
                                            <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => startEditStatusMapping(row)}>
                                                Изменить
                                            </button>
                                            <button type="button" className="rzm-btn rzm-btn-sm" onClick={() => deleteStatusMappingRow(row)}>
                                                Удалить
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                </>
            )}

        </div>
    );
};

export default RozetkaIntegrationPage;
